import React, {useState, useEffect, useRef} from 'react';
import styles from './index.module.less';
import Markdown from "../../../components/Markdown";
import {
  Alert,
  Button,
  ButtonGroup,
  Classes, Collapse,
  Colors,
  Drawer,
  Icon, Intent,
  Menu,
  MenuItem,
  Position,
  ProgressBar
} from "@blueprintjs/core";
import {useHistory, useParams} from 'react-router-dom';
import SingleChooseQuestion from "../../../components/SingleChooseQuestion";
import MultipleChooseQuestion from "../../../components/MultipleChooseQuestion";
import CodingQuestion from "../../../components/CodingQuestion";
import classes from 'classnames';
import { useScroll } from 'ahooks';
import {useDispatch, useSelector} from "react-redux";
import constant from "../../../store/constant";
import axios from 'axios';
import emitter from "../../../utils/emitter";
import JudgeCases from "../../../components/JudgeCases";

const ArticleReader = ({ lesson, setLesson }) => {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [rightAnswerCollapseOpen, setRightAnswerCollapseOpen] = useState(false);
  const rightAnswerCodeBlockRef = useRef();
  const container = useRef(null);
  const { top: scrollTop } = useScroll(container);
  const { chapterId, chapterName, lessonId } = useParams();
  const chapter = useSelector(state => state.chapter);
  const dispatch = useDispatch();
  const history = useHistory();
  const heartbeatTimer = useRef(0);
  const completedVerified = useRef(false);
  const courseEntry = useSelector(state => state.courseEntry);
  const [lessonProgress, setLessonProgress] = useState(0);
  const [nextChapterAlertOpen, setNextChapterAlertOpen] = useState(false);
  const practiceCodeInitialized = useRef(false);

  useEffect(() => {
    if (chapter.catalogs.length === 0) {
      return;
    }
    const result = chapter.catalogs.reduce((result, catalog) => {
      if (catalog.status === 'done') {
        result[0] += 1;
      }
      result[1] += 1;
      return result;
    }, [0, 0]);
    setLessonProgress(result[0] / result[1]);
  }, [chapter.catalogs]);

  useEffect(() => {
    if (rightAnswerCollapseOpen === true && rightAnswerCodeBlockRef.current) {
      setTimeout(() => {
        window.hljs.highlightBlock(rightAnswerCodeBlockRef.current);
      }, 100);
    }
  }, [rightAnswerCollapseOpen]);

  // 1. 切换章节时，滚动到最顶部
  // 2. 发送心跳
  useEffect(() => {
    completedVerified.current = false;
    container.current.scrollTo({ top: 0 });
    heartbeat();
    document.addEventListener('visibilitychange', (e) => {
      if (document.visibilityState === 'visible') {
        heartbeat();
      } else {
        window.clearTimeout(heartbeatTimer.current);
      }
    });
    axios.post('/api/course/learn_progress_submit', {
      object_id: lessonId,
      type: 'lesson',
      status: 'doing'
    }).then();
    return () => {
      window.clearTimeout(heartbeatTimer.current);
    }
  }, [lessonId]);

  // 判断文章是否阅读完成
  useEffect(() => {
    if (lesson.loading) {
      return;
    }
    // 如果课程是完成状态，直接return
    if (lesson.status === 'done') {
      return;
    }
    // 判断练习题是否完成
    const exerciseFinished = lesson.exercises.reduce((result, exercise) => {
      return exercise.status === 'right' && result;
    }, true);
    if (exerciseFinished && lesson.readFinished) {
      completedVerified.current = true;
      // 修改外层目录
      dispatch({
        type: constant.courseEntry.SET_CATALOGS,
        payload: courseEntry.catalogs.map(part => {
          return {
            ...part,
            chapters: (part.chapters || []).map(cpt => {
              if (String(cpt.id) === String(chapterId)) {
                return {
                  ...cpt,
                  lessons: (cpt.lessons || []).map(ls => {
                    if (String(ls.id) === String(lessonId)) {
                      return {
                        ...ls,
                        status: 'done'
                      }
                    }
                    if (String(ls.id) === String(lesson.next)) {
                      return {
                        ...ls,
                        permission: 'can_learn'
                      }
                    }
                    return ls;
                  })
                }
              }
              return cpt;
            })
          }
        })
      });
      // 修改课程的状态为完成
      setLesson({
        ...lesson,
        status: 'done'
      });
      // 修改章节目录当前课程为完成
      dispatch({
        type: constant.chapter.SET_CATALOGS,
        payload: chapter.catalogs.map(catalog => {
          if (String(catalog.id) === String(lessonId)) {
            catalog.status = 'done';
          }
          if (String(catalog.id) === String(lesson.next)) {
            catalog.permission = 'can_learn';
          }
          return catalog;
        })
      });
      // 如果已经是最后一小节，则弹出对话框，引导用户学习下一章
      if (!lesson.next && lesson.nextChapter.chapterId) {
        setNextChapterAlertOpen(true);
      }
      axios.post('/api/course/learn_progress_submit', {
        object_id: lessonId,
        type: 'lesson',
        status: 'done'
      }).then();
    }
  }, [lesson, dispatch, courseEntry.catalogs]);

  useEffect(() => {
    if (!container.current) {
      return;
    }
    if (lesson.readFinished === true) {
      return;
    }
    if (lesson.loading === true) {
      return;
    }
    const { scrollHeight, offsetHeight } = container.current;
    if (Math.abs(scrollTop - (scrollHeight - offsetHeight)) < 20) {
      lesson.readFinished = true;
      axios.post('/api/course/learn_progress_submit', {
        object_id: lessonId,
        type: 'lesson',
        status: 'readed'
      }).then();
      setLesson({ ...lesson });
    }
  }, [setLesson, scrollTop, lesson, dispatch, lessonId]);

  useEffect(() => {
    if (lesson.type !== 'code_problem') {
      return;
    }
    const [exercise] = lesson.exercises || [];
    if (practiceCodeInitialized.current === true || !exercise) {
      return;
    }
    if (lesson.loading === true) {
      return;
    }
    practiceCodeInitialized.current = true;
    setTimeout(() => {
      emitter.emit('copy-question-code', {
        code: exercise.code,
        defaultCode: exercise.defaultCode,
        id: exercise.id,
        title: exercise.title,
        status: exercise.status,
        fixed: true
      });
    }, 500);
  }, [lesson]);

  const heartbeat = () => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    axios.post('/api/course/learn_time_record', {
      "object_id": lessonId,
      "type": "lesson",
      "time_second": 10
    }).then(() => {
      window.clearTimeout(heartbeatTimer.current);
      heartbeatTimer.current = setTimeout(() => heartbeat(), 10000);
    })
  };

  const handleChangeArticle = (id) => {
    setCatalogOpen(false);
    practiceCodeInitialized.current = false;
    history.push(`/learn/${chapterName}/${chapterId}/${id}`);
  };
  const handleClickPrevBtn = () => {
    practiceCodeInitialized.current = false;
    history.push(`/learn/${chapterName}/${chapterId}/${lesson.prev}`);
  };
  const handleClickNextBtn = () => {
    practiceCodeInitialized.current = false;
    history.push(`/learn/${chapterName}/${chapterId}/${lesson.next}`);
  };
  const handleClickNextChapterBtn = () => {
    practiceCodeInitialized.current = false;
    const { chapterName: nextChapterName, chapterId: nextChapterId, lessonId: nextLessonId } = lesson.nextChapter;
    history.push(`/learn/${nextChapterName}/${nextChapterId}/${nextLessonId}`);
  };

  const renderExercise = (exercise, index) => {
    if (lesson.type === 'code_problem') {
      return null;
    }
    if (exercise.type === 'code_check' || exercise.type === 'code_problem') {
      return <CodingQuestion lessonId={lessonId} key={index} exercise={exercise} />
    }
    if (exercise.type === 'radio') {
      return <SingleChooseQuestion key={index} exercise={exercise} />
    }
    if (exercise.type === 'checkbox') {
      return <MultipleChooseQuestion key={index} exercise={exercise} />
    }
  };

  const renderMenuIcon = (item) => {
    if (item.permission === 'can_not_learn') {
      return <Icon icon='lock' />
    }
    if (item.status === 'done') {
      return (
        <Icon icon='tick-circle' color={Colors.GREEN5} />
      )
    }
    if (item.status === 'doing') {
      return (
        <Icon icon='tick-circle' color={Colors.ORANGE5} />
      )
    }
    return (
      <Icon icon='tick-circle' color={Colors.GRAY5} />
    );
  };

  const handleRedirectToShare = () => {
    window.sessionStorage.setItem('share-info', JSON.stringify({
      pathId: chapterId,
      next: lesson.nextChapter,
    }));
    history.push('/part-finished');
  };

  const renderNextButton = () => {
    if (chapter.catalogs.length === 0) {
      return null;
    }
    if (String(lessonId) === String(chapter.catalogs[chapter.catalogs.length - 1].id)) {
      return (
        <Button
          className={classes('next-lesson', lesson.loading && Classes.SKELETON)}
          onClick={handleRedirectToShare}
          rightIcon='arrow-right'
          disabled={lesson.status !== 'done'}
        >继续</Button>
      )
    }
    return (
      <Button
        className={classes('next-lesson', lesson.loading && Classes.SKELETON)}
        onClick={handleClickNextBtn}
        rightIcon='arrow-right'
        disabled={!lesson.next || lesson.status !== 'done'}
      >
        下一节
      </Button>
    )
  };

  lesson.sections = lesson.sections || [];
  lesson.exercises = lesson.exercises || [];
  lesson.conceptReview = lesson.conceptReview || {};

  return (
    <>
      <div className={classes(styles['article-reader'], Classes.TEXT_LARGE, 'article-reader')}>
        <article className={classes(styles.main)} ref={container}>
          <h1 className={classes(Classes.HEADING, lesson.loading && Classes.SKELETON)}>{lesson.title}</h1>
          <div className='article'>
            {lesson.sections.map((section, index) => <Markdown loading={lesson.loading} key={index} content={section.content} />)}
          </div>
          {lesson.judgeCases && (
            <JudgeCases cases={lesson.judgeCases} />
          )}
          {lesson.expectCode && (
            <div className={styles['article-expect-answer']}>
              <Button intent={Intent.WARNING} onClick={() => setRightAnswerCollapseOpen(!rightAnswerCollapseOpen)}>查看参考答案</Button>
              <Collapse isOpen={rightAnswerCollapseOpen}>
              <pre ref={rightAnswerCodeBlockRef} className={classes('bp3-code-block', 'bp3-text-large')}>
                <code className='python'>{lesson.expectCode}</code>
              </pre>
              </Collapse>
            </div>
          )}
          <div className='exercise'>
            {lesson.exercises.map((exercise, index) => renderExercise(exercise, index))}
          </div>
          <Markdown loading={lesson.loading} content={lesson.conceptReview.content} />
        </article>
        <div className={styles.actions}>
          <div className={classes(styles.left)}>
            <Button className={classes('chapter-catalog', lesson.loading && Classes.SKELETON)} icon='menu' onClick={() => setCatalogOpen(true)} />
          </div>
          <div className={styles.middle}>
            <ProgressBar value={lessonProgress} animate={false} stripes={false} intent={Intent.SUCCESS} />
          </div>
          <div className={styles.right}>
            <ButtonGroup>
              <Button className={classes('prev-lesson', lesson.loading && Classes.SKELETON)} onClick={handleClickPrevBtn} icon='arrow-left' disabled={!lesson.prev}>上一节</Button>
              {renderNextButton()}
            </ButtonGroup>
          </div>
        </div>
      </div>
      <Drawer onClose={() => setCatalogOpen(false)} icon='menu' title={chapterName} className={Classes.DARK} isOpen={catalogOpen} canEscapeKeyClose canOutsideClickClose position={Position.LEFT} size={Drawer.SIZE_SMALL}>
        <div className={Classes.DRAWER_BODY}>
          <Menu className={Classes.ELEVATION_0} large>
            {chapter.catalogs.map((catalog, index) => (
              <MenuItem
                onClick={() => handleChangeArticle(catalog.id)}
                active={Number(catalog.id) === Number(lessonId)}
                icon={renderMenuIcon(catalog)}
                key={index}
                text={`${index + 1}. ${catalog.title}`}
                label={catalog.label}
                disabled={catalog.permission === 'can_not_learn'}
              />
            ))}
          </Menu>
        </div>
      </Drawer>
      <Alert
        className={Classes.DARK}
        intent={Intent.SUCCESS}
        icon='box'
        cancelButtonText='关闭'
        confirmButtonText='立即学习下一章'
        isOpen={nextChapterAlertOpen}
        onConfirm={handleClickNextChapterBtn}
        onClose={() => setNextChapterAlertOpen(false)}
      >
        <p>恭喜，当前章节已经学习完成，你可以点击下方按钮进入下一章的学习！</p>
      </Alert>
    </>
  );
};

export default ArticleReader;
